import { RulebookVisibility } from "@next-platform/packages-ts-types";
const META_KEYS = ["cover_url", "tags", "visibility", "definition_visible"];
/** Server records always carry these; drafts may leave them unset. Compare with the server defaults applied. */
const withMetaDefaults = (meta) => ({
    tags: [],
    visibility: RulebookVisibility.PRIVATE,
    definition_visible: false,
    ...meta,
});
/** JSON with object keys sorted, so a server copy (whose stored maps come back in any key order) compares equal. */
const stableStringify = (value) => {
    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(",")}]`;
    }
    if (value !== null && typeof value === "object") {
        const entries = Object.entries(value)
            .filter(([, entry]) => entry !== undefined)
            .sort(([left], [right]) => left.localeCompare(right, "en"));
        return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(",")}}`;
    }
    return JSON.stringify(value ?? null);
};
const isSame = (first, second) => stableStringify(first) === stableStringify(second);
/** Added, removed and edited entries between two id-keyed lists, plus one change when the shared order moved. */
const countListChanges = (baseline, current) => {
    const baselineById = new Map(baseline.map((entry) => [entry.id, entry]));
    const currentIds = new Set(current.map((entry) => entry.id));
    const removed = baseline.filter((entry) => !currentIds.has(entry.id)).length;
    const addedOrEdited = current.filter((entry) => !isSame(baselineById.get(entry.id), entry)).length;
    const sharedBaselineOrder = baseline.filter((entry) => currentIds.has(entry.id)).map((entry) => entry.id);
    const sharedCurrentOrder = current.filter((entry) => baselineById.has(entry.id)).map((entry) => entry.id);
    return removed + addedOrEdited + (isSame(sharedBaselineOrder, sharedCurrentOrder) ? 0 : 1);
};
/**
 * Number of user-visible edits between the published baseline and the draft: each changed property, meter
 * or combined rule counts once (so "3 unpublished changes" reads the way creators think about their edits).
 */
export default function countRulebookChanges({ baseline, current }) {
    const baselineMeta = withMetaDefaults(baseline.meta);
    const currentMeta = withMetaDefaults(current.meta);
    const propertyChanges = [
        isSame(baseline.definition.name, current.definition.name),
        isSame(baseline.definition.description, current.definition.description),
        ...META_KEYS.map((key) => isSame(baselineMeta[key], currentMeta[key])),
    ].filter((same) => !same).length;
    return (propertyChanges +
        countListChanges(baseline.definition.meters, current.definition.meters) +
        countListChanges(baseline.definition.combined_rules, current.definition.combined_rules));
}
